import React, {useMemo, useState} from "react";
import {compose} from "redux";
import {translate} from "react-i18next";
import {connect} from "react-redux";
import {
    changeDataflowPublishingWizardHeaderForm,
    clearDataflowPublishingHeaderInfo,
    deleteCustomHeader,
    saveCustomHeader,
} from "./actions";
import {
    GUTTER_MD,
    GUTTER_SM,
    MARGIN_MD,
    MODAL_WIDTH_XL,
    SPAN_HALF,
    SPAN_ONE_THIRD,
    SPAN_TWO_THIRDS
} from "../../../styles/constants";
import {Button, Checkbox, Col, Form, Input, Row} from "antd";
import EnhancedModal from "../../../components/enhanced-modal";
import _ from "lodash";
import {isIDValidWithHelp} from "../../../utils/artefactValidators";
import DataflowHeaderCard from "./DataflowHeaderCard";

const formItemLayout = {
    labelCol: {span: SPAN_ONE_THIRD},
    wrapperCol: {span: SPAN_TWO_THIRDS}
};

const mapStateToProps = state => {
    return {
        dataflowHeaderInfo: state.scenes.mappingHandler.dataflowPublishing.dataflowHeaderInfo,
    };
};

const mapDispatchToProps = dispatch => ({
    clearDataflowInfo: () => dispatch(clearDataflowPublishingHeaderInfo()),
    onChange: fields => dispatch(changeDataflowPublishingWizardHeaderForm(fields)),
    onSave: (header) => dispatch(saveCustomHeader(header)),
    onDelete: (headerEntityId, dataflowTriplet) => dispatch(deleteCustomHeader(headerEntityId, dataflowTriplet)),
});

const mapPropsToFields = ({dataflowHeaderInfo}) => ({
    test: Form.createFormField({
        value: (dataflowHeaderInfo && dataflowHeaderInfo.test) || null
    }),
    name: Form.createFormField({
        value: (dataflowHeaderInfo && dataflowHeaderInfo.name) || null
    }),
    sender: {
        organisationId: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.organisationId) || null
        }),
        organisationName: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.organisationName) || null
        }),
        contactName: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.contactName) || null
        }),
        contactDepartment: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.contactDepartment) || null
        }),
        contactRole: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.contactRole) || null
        }),
        contactEmail: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.sender && dataflowHeaderInfo.sender.contactEmail) || null
        })
    },
    receiver: {
        organisationId: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.organisationId) || null
        }),
        organisationName: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.organisationName) || null
        }),
        contactName: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.contactName) || null
        }),
        contactDepartment: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.contactDepartment) || null
        }),
        contactRole: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.contactRole) || null
        }),
        contactEmail: Form.createFormField({
            value: (dataflowHeaderInfo && dataflowHeaderInfo.receiver && dataflowHeaderInfo.receiver.contactEmail) || null
        })
    },
    dataSetAgencyId: Form.createFormField({value: (dataflowHeaderInfo && dataflowHeaderInfo.dataSetAgencyId) || null}),
    source: Form.createFormField({value: (dataflowHeaderInfo && dataflowHeaderInfo.source) || null})
});

const onFieldsChange = ({onChange}, fields) =>
    onChange({
        test:
            fields.test !== undefined
                ? fields.test.value
                : undefined,
        name:
            fields.name
                ? fields.name.value
                : undefined,
        sender:
            fields.sender !== undefined
                ? _.mapValues(fields.sender, field => field.value)
                : undefined,
        receiver:
            fields.receiver !== undefined
                ? _.mapValues(fields.receiver, field => field.value)
                : undefined,
        dataSetAgencyId:
            fields.dataSetAgencyId !== undefined
                ? fields.dataSetAgencyId.value
                : undefined,
        source:
            fields.source !== undefined
                ? fields.source.value
                : undefined
    });

const DataflowHeader = ({t, dataflowHeaderInfo, clearDataflowInfo, form, disabled = false, onSave, onDelete, artefacts}) => {
    const isReceiverDisabled = useMemo(() => !dataflowHeaderInfo || !dataflowHeaderInfo.receiver ||
        !dataflowHeaderInfo.receiver.organisationId || dataflowHeaderInfo.receiver.organisationId.length === 0 ||
        disabled, [dataflowHeaderInfo]);

    const hasHeaderAnnotation = useMemo(() => {
        if (!artefacts || !dataflowHeaderInfo || !dataflowHeaderInfo.dataflowTriplet) {
            return false;
        }
        const selectedDataflow = artefacts.find(dataflow => dataflow.id === dataflowHeaderInfo.dataflowTriplet.id && dataflow.agencyId === dataflowHeaderInfo.dataflowTriplet.agencyId && dataflow.version === dataflowHeaderInfo.dataflowTriplet.version);
        if (!selectedDataflow) {
            return false;
        }
        return selectedDataflow.hasCustomHeader;
    }, [artefacts, dataflowHeaderInfo]);

    const [hasHeader, setHasHeader] = useState(null);
    const hasHeaderFlag = useMemo(() => hasHeader === null ? hasHeaderAnnotation : hasHeader, [hasHeader, hasHeaderAnnotation]);

    return (
        <EnhancedModal
            visible={!!dataflowHeaderInfo}
            onCancel={() => {
                setHasHeader(null);
                clearDataflowInfo();
            }}
            width={MODAL_WIDTH_XL}
            title={t('scenes.mappingHandler.DataflowPublishing.headerAction', {id: dataflowHeaderInfo ? `[${dataflowHeaderInfo.dataflowTriplet.id}]` : ""})}
            footer={
                <Row type="flex" justify="end" align="middle" gutter={GUTTER_SM}>
                    <Col>
                        <Button
                            onClick={() => {
                                setHasHeader(null);
                                clearDataflowInfo();
                            }}>{t('commons.buttons.close.title')}
                        </Button>
                    </Col>
                    <Col>
                        <Button
                            type="primary"
                            onClick={() => {
                                if (hasHeaderFlag) {
                                    onSave(dataflowHeaderInfo);
                                } else {
                                    if (!!dataflowHeaderInfo.entityId) onDelete(dataflowHeaderInfo.entityId, dataflowHeaderInfo.dataflowTriplet)
                                }
                                setHasHeader(null);
                                clearDataflowInfo();
                            }}>{t('commons.buttons.save.title')}
                        </Button>
                    </Col>
                </Row>
            }
        >
            <Form className="advanced-form">
                <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD}}>
                    <Col span={SPAN_HALF}>
                        <Form.Item
                            label={t('scenes.dataManager.dataflowBuilder.wizard.headerForm.isVisibleCheckbox.label')}
                        >
                            <Checkbox onChange={e => setHasHeader(!!e.target.checked)} checked={hasHeaderFlag}
                                      disabled={disabled}/>
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={GUTTER_MD} style={{marginBottom: MARGIN_MD, visibility: !hasHeaderFlag ? "hidden" : "visible"}}>
                    <Col span={SPAN_HALF}>
                        <Form.Item label={t('data.dataflow.header.test.label')}>
                            {form.getFieldDecorator('test', {valuePropName: 'checked'})(<Checkbox
                                disabled={disabled}/>)}
                        </Form.Item>
                    </Col>
                    <Col span={SPAN_HALF}>
                        <Form.Item label={t('data.dataflow.header.name.label')} {...formItemLayout}>
                            {form.getFieldDecorator('name')(<Input title={form.getFieldValue('name')}
                                                                   disabled={disabled}/>)}
                        </Form.Item>
                    </Col>
                </Row>
                <DataflowHeaderCard title={t('data.dataflow.header.sender.label')} form={form} type="sender"
                                    disabled={disabled} style={{visibility: !hasHeaderFlag ? "hidden" : "visible"}}/>
                <DataflowHeaderCard title={t('data.dataflow.header.receiver.label')} form={form} type="receiver"
                                    disabled={disabled} receiverDisabled={isReceiverDisabled} style={{visibility: !hasHeaderFlag ? "hidden" : "visible"}}/>
                <Row gutter={GUTTER_MD} style={{visibility: !hasHeaderFlag ? "hidden" : "visible"}}>
                    <Col span={SPAN_HALF}>
                        <Form.Item
                            label={t('data.dataflow.header.dataSetAgencyId.label')}
                            validateStatus={form.getFieldValue('dataSetAgencyId') && isIDValidWithHelp(t, form.getFieldValue('dataSetAgencyId')).help ? "warning" : null}
                            help={form.getFieldValue('dataSetAgencyId') && isIDValidWithHelp(t, form.getFieldValue('dataSetAgencyId')).help}
                            {...formItemLayout}
                        >
                            {form.getFieldDecorator('dataSetAgencyId', {
                                getValueFromEvent: e => e.target.value.toUpperCase()
                            })(
                                <Input
                                    title={form.getFieldValue('dataSetAgencyId')}
                                    disabled={disabled}
                                    style={{textTransform: "uppercase"}}
                                />
                            )}
                        </Form.Item>
                    </Col>
                    <Col span={SPAN_HALF}>
                        <Form.Item label={t('data.dataflow.header.source.label')} {...formItemLayout}>
                            {form.getFieldDecorator('source')(<Input title={form.getFieldValue('source')}
                                                                     disabled={disabled}/>)}
                        </Form.Item>
                    </Col>
                </Row>
            </Form>
        </EnhancedModal>
    )
}

export default compose(
    translate(),
    connect(mapStateToProps, mapDispatchToProps),
    Form.create({
        mapPropsToFields,
        onFieldsChange
    })
)(DataflowHeader);
